/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { ref } from 'ember-ref-bucket';
import { type Registry as Services, service } from '@ember/service';
import { action } from '@ember/object';
import { runTask } from 'ember-lifeline';
import { modifier } from 'ember-modifier';
import { UploadFile } from 'ember-file-upload';
import { guidFor } from '@ember/object/internals';
import { waitFor } from '@ember/test-waiters';
import calculatePosition, {
  type CalculatePositionOptions,
} from 'ember-basic-dropdown/utils/calculate-position';
import { isNone } from '@ember/utils';
import config from 'uplisting-frontend/config/environment';
import { AnalyticsEvents } from 'uplisting-frontend/services/analytics';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import type { IAssignableUser } from 'uplisting-frontend/models/inbox-message';
import { ONE_MB } from 'uplisting-frontend/utils/file-sizes';
import BookingModel from 'uplisting-frontend/models/booking';
import BookingRequestModel from 'uplisting-frontend/models/booking-request';
import type { RhsRecord } from 'uplisting-frontend/utils/rhs/types';
import EnquiryModel from 'uplisting-frontend/models/enquiry';
import { getRhsRecordKey } from 'uplisting-frontend/utils/rhs/helpers';
import {
  getSaveReplyRouteFor,
  getEditDetailsRouteFor,
  getSavedRepliesRouteFor,
} from 'uplisting-frontend/utils/rhs/routes';
import { getCssVariable, fromPx } from 'uplisting-frontend/utils/css';
import { type FeedbackResult } from 'uplisting-frontend/components/ui/ai-draft-feedback/index';
import { MessageStatus, ContextType } from 'uplisting-frontend/models/message';
import type {
  ConflictSource,
  ConflictSourceType,
} from 'uplisting-frontend/models/ai-draft';

const SOURCE_PRECEDENCE: ConflictSourceType[] = [
  'host_instructions',
  'knowledge_base_qa_pair',
  'property_data',
  'stay_data',
];

const MESSAGE_POLLING_INTERVALS: number[] = [
  10_000, 20_000, 30_000, 60_000, 60_000,
];

const VRBO_ATTACHMENT_ACCEPT_TYPES: string = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/bmp',
  'image/svg+xml',
  'image/tiff',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
  'text/csv',
  'text/plain',
].join(',');

const PREVIEWABLE_IMAGE_TYPES: string[] = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/bmp',
  'image/svg+xml',
];

interface IArgs {
  record: RhsRecord;
  reply?: ModelRegistry['contextual-reply'];
}

interface BookingMessagesSignature {
  Element: HTMLDivElement;

  Args: IArgs;

  Blocks: {
    actions?: [];
  };
}

export default class UiBookingMessagesComponent extends Component<BookingMessagesSignature> {
  @service intl!: Services['intl'];
  @service store!: Services['store'];
  @service session!: Services['session'];
  @service router!: Services['router'];
  @service sentry!: Services['sentry'];
  @service analytics!: Services['analytics'];
  @service fileHandler!: Services['file-handler'];
  @service notifications!: Services['notifications'];
  @service featureFlags!: Services['feature-flags'];
  @service features!: Services['features'];

  @service('repositories/message')
  messageRepository!: Services['repositories/message'];

  @service('repositories/contextual-reply')
  contextualReplyRepository!: Services['repositories/contextual-reply'];

  @service('repositories/ai-setting')
  aiSettingRepository!: Services['repositories/ai-setting'];

  @ref('messagesContainer') messagesContainer!: HTMLDivElement;

  MAX_FILE_SIZE = 20 * ONE_MB;
  VRBO_MAX_FILE_SIZE = 5 * ONE_MB;

  @tracked isSendingMessage = false;
  @tracked isUploadingImage = false;
  @tracked uploadedImage?: string | null = '';
  @tracked uploadedImagePreview?: string | null = '';
  @tracked uploadedMediaType?: string | null = '';
  @tracked uploadedFileName?: string | null = '';
  @tracked isGeneratingAIDraft = false;
  @tracked showFeedbackModal = false;
  @tracked showConflictsModal = false;
  @tracked currentAIDraft?: ModelRegistry['ai-draft'];
  @tracked pendingAiMessage?: ModelRegistry['message'];
  @tracked hasPendingDraftLoaded = false;
  @tracked showTranslations = false;
  @tracked isAssigningUser = false;
  @tracked isAssignableUsersUnavailable = false;

  get hasTranslatedMessages(): boolean {
    return this.messagesSorted.some((m) => m.hasTranslation);
  }

  get hasActiveConflicts(): boolean {
    return this.currentAIDraft?.hasConflicts ?? false;
  }

  get activeConflicts() {
    return this.currentAIDraft?.conflictsDetected ?? [];
  }

  get processedConflicts() {
    return this.activeConflicts.map((conflict) => {
      const ordered = SOURCE_PRECEDENCE.map((type) =>
        conflict.sources.find((s) => s.type === type),
      ).filter((s): s is ConflictSource => Boolean(s));
      return {
        ...conflict,
        sources: ordered.map((source, index) => ({
          ...source,
          isWinner: index === 0,
        })),
      };
    });
  }

  @tracked pendingFeedback?: {
    draft: ModelRegistry['ai-draft'];
    action: 'discard' | 'regenerate' | 'sent_feedback';
    requireReason: boolean;
  };

  get feedbackRequireReason(): boolean {
    return this.pendingFeedback?.requireReason ?? false;
  }

  @cached
  get aiDraftConfidenceLabel(): string | undefined {
    const level = this.currentAIDraft?.confidenceLevel;

    if (!level) {
      return;
    }

    return this.intl.t(`action_bookings_messages.ai.confidence_level.${level}`);
  }

  @cached
  get changeset(): GenericChangeset<ModelRegistry['message']> {
    return this.messageRepository.buildChangeset({
      content: this.args.reply?.content ?? '',
      type: this.selectedMessageType,
    });
  }

  @cached
  get record(): RhsRecord {
    return this.args.record;
  }

  @cached
  get isBooking(): boolean {
    return this.record instanceof BookingModel;
  }

  @cached
  get isBookingRequest(): boolean {
    return this.record instanceof BookingRequestModel;
  }

  @cached
  get isEnquiry(): boolean {
    return this.record instanceof EnquiryModel;
  }

  @cached
  get booking(): ModelRegistry['booking'] {
    return this.record as ModelRegistry['booking'];
  }

  @cached
  get bookingRequest(): ModelRegistry['booking-request'] {
    return this.record as ModelRegistry['booking-request'];
  }

  @cached
  get enquiry(): ModelRegistry['enquiry'] {
    return this.record as ModelRegistry['enquiry'];
  }

  @cached
  get messagesSorted(): ModelRegistry['message'][] {
    const messages = this.record.messagesSorted;

    if (this.pendingAiMessage && !messages.includes(this.pendingAiMessage)) {
      return [...messages, this.pendingAiMessage];
    }

    return messages;
  }

  @cached
  get canUploadAirbnbAttachment(): boolean {
    return (
      this.record.isAirbnbOfficial &&
      !this.isReadOnlyAirbnb &&
      this.isAirbnbSelected
    );
  }

  @cached
  get canUploadVrboAttachment(): boolean {
    return this.record.isVrbo && this.isVrboSelected;
  }

  @cached
  get canUploadImage(): boolean {
    // Media is only allowed on confirmed-booking threads: both Airbnb and Vrbo
    // reject attachments on pre-booking threads (enquiries and pending booking
    // requests), so the upload control is hidden for the other occasion types.
    return (
      this.isBooking &&
      (this.canUploadAirbnbAttachment || this.canUploadVrboAttachment)
    );
  }

  get attachmentAcceptTypes(): string {
    return this.canUploadVrboAttachment
      ? VRBO_ATTACHMENT_ACCEPT_TYPES
      : 'image/jpeg,image/png,image/gif,video/mp4,video/quicktime';
  }

  get attachmentMaxFileSize(): number {
    return this.canUploadVrboAttachment
      ? this.VRBO_MAX_FILE_SIZE
      : this.MAX_FILE_SIZE;
  }

  get attachmentLabel(): string {
    return this.intl.t(
      this.canUploadVrboAttachment
        ? 'action_bookings_messages.messages.actions.attach_file'
        : 'action_bookings_messages.messages.actions.attach_image',
    );
  }

  get isUploadedImagePreviewable(): boolean {
    return PREVIEWABLE_IMAGE_TYPES.includes(this.uploadedMediaType ?? '');
  }

  @cached
  get imageUploadId(): string {
    return guidFor(this);
  }

  @cached
  get isMessageChannelDisabled(): boolean {
    if (this.isAutoReplyActive) return true;

    return !(this.selectedChannel?.enabled && this.canSendToAnyChannel);
  }

  @cached
  get isAIDraftButtonDisabled(): boolean {
    return this.isGeneratingAIDraft || this.isMessageChannelDisabled;
  }

  @cached
  get isAiDraftEnabled(): boolean {
    return (
      this.featureFlags.gate.enabled('ai_messaging') &&
      !!this.session.currentUser?.hasAccessTo('ai_drafts.create')
    );
  }

  @cached
  get isAutoReplyActive(): boolean {
    if (!this.isBooking) return false;

    const contextValue = this.booking.aiOccasionContext?.autoReplyEnabled;
    if (contextValue !== null && contextValue !== undefined)
      return contextValue;

    const propertyId = this.record.belongsTo('property').id();
    return this.aiSettingRepository.resolveAutoReplyForProperty(propertyId);
  }

  get composerMessageAreaClass(): string {
    const classes: string[] = [];

    if (this.currentAIDraft) {
      classes.push('ai-draft-active');
    }

    if (this.canSendRequest || this.isMessageChannelDisabled) {
      classes.push('send-request-active');
    }

    return classes.join(' ');
  }

  get showComposerDraftButton(): boolean {
    return (
      this.isAiDraftEnabled &&
      !this.isMessageChannelDisabled &&
      !this.canSendRequest &&
      !this.currentAIDraft &&
      !this.isGeneratingAIDraft
    );
  }

  get showComposerSendButton(): boolean {
    return !this.isMessageChannelDisabled;
  }

  get showComposerInfoRow(): boolean {
    return !this.canSendRequest;
  }

  @cached
  get messagePlaceholder(): string {
    const { intl, selectedChannel } = this;
    const keyBase = 'action_bookings_messages.messages';

    if (this.isAutoReplyActive) {
      return intl.t(`${keyBase}.auto_reply_active`);
    }

    if (selectedChannel && !selectedChannel.enabled) {
      const airbnbReasonKey = `${keyBase}.channel_disabled_reasons_airbnb.${selectedChannel.reason}`;

      if (this.record.isAirbnbOfficial && intl.exists(airbnbReasonKey)) {
        return intl.t(airbnbReasonKey);
      }

      const reasonKey = `${keyBase}.channel_disabled_reasons.${selectedChannel.reason}`;

      if (intl.exists(reasonKey)) {
        return intl.t(reasonKey);
      }

      if (this.isSmsSelected) {
        return intl.t(`${keyBase}.channel_disabled_reasons.sms_fallback`);
      }

      return intl.t(reasonKey);
    }

    if (this.isAiDraftEnabled) {
      return intl.t(`${keyBase}.placeholder_with_ai`);
    }

    const name =
      this.record.guestName ?? intl.t(`${keyBase}.default_guest_name`);

    return intl.t(`${keyBase}.placeholder`, { name });
  }

  @cached
  get quickResponseOptions(): { label: string; prompt: string }[] {
    const { intl } = this;
    const keyBase = 'action_bookings_messages.messages.quick_response';

    return [
      { label: intl.t(`${keyBase}.yes_confirm`), prompt: 'Approve request' },
      { label: intl.t(`${keyBase}.no_deny`), prompt: 'Decline request' },
      { label: intl.t(`${keyBase}.acknowledge`), prompt: 'Looking into it' },
    ];
  }

  @cached
  get isAirbnbSelected(): boolean {
    return ['airbnb', 'airbnb_official'].includes(
      this.selectedChannel?.id ?? '',
    );
  }

  @cached
  get isVrboSelected(): boolean {
    return this.selectedChannel?.id === 'home_away';
  }

  @cached
  get isSmsSelected(): boolean {
    return this.selectedChannel?.id === 'sms';
  }

  @cached
  get isEmailSelected(): boolean {
    return this.selectedChannel?.id === 'email';
  }

  @cached
  get canSendMessages(): boolean {
    return (
      this.session.currentUser?.hasAccessTo('bookings.messages.create') ?? false
    );
  }

  @cached
  get inboxMessage(): ModelRegistry['inbox-message'] | undefined {
    return this.record.inboxMessage ?? undefined;
  }

  @cached
  get showAssignedUserSelect(): boolean {
    return (
      this.features.isInboxAssignmentEnabled &&
      !!this.inboxMessage &&
      (this.session.currentUser?.hasAccessTo('bookings.messages') ?? false)
    );
  }

  @cached
  get assignableUsers(): IAssignableUser[] {
    return this.inboxMessage?.assignableUsers ?? [];
  }

  @cached
  get isReadOnlyAirbnb(): boolean {
    const { record } = this;

    return record.property.hasReadOnlyAirbnbLink && record.isAirbnbOfficial;
  }

  @cached
  get messageNotSendable(): boolean {
    if (this.isReadOnlyAirbnb) {
      return true;
    }

    const contentMissing = !this.changeset.content && !this.uploadedImage;
    const contentInvalid = this.changeset.isInvalid;

    return contentMissing || contentInvalid;
  }

  @cached
  get isMessageChannelSelectable(): boolean {
    return !this.isEnquiry;
  }

  @cached
  get hasContentOrImage(): boolean {
    return this.changeset.content.length > 0 || Boolean(this.uploadedImage);
  }

  @cached
  get isSubmitMessageDisabled(): boolean {
    return this.isSendingMessage || this.messageNotSendable;
  }

  @cached
  get canSendToAnyChannel(): boolean {
    if (this.isAutoReplyActive || this.isReadOnlyAirbnb) {
      return false;
    }

    return this.messageTypeOptions.length > 0;
  }

  @cached
  get messageTypeOptions(): string[] {
    const options = this.record.messagingChannels
      .filter((channel) => channel.enabled || channel.reason)
      .map((channel) => channel.id);

    // Ensure SMS is always the last option so we can display char count next to it
    const isSmsLast = options[options.length - 1] === 'sms';

    if (options.includes('sms') && !isSmsLast) {
      return [...options.filter((o) => o !== 'sms'), 'sms'];
    }

    return options;
  }

  @cached
  get selectedMessageType(): string | undefined {
    const channel = this.record.messagingChannels.find(
      (channel) => channel.enabled && channel.usedForLastMessage,
    );

    if (channel) {
      return this.messageTypeOptions.find((option) => option === channel.id);
    }

    return this.messageTypeOptions[0];
  }

  @cached
  get selectedChannel(): ModelRegistry['messaging-channel'] | undefined {
    return this.record.messagingChannels.find(
      (channel) => channel.id === this.changeset.type,
    );
  }

  @cached
  get canSendRequest(): boolean {
    const { selectedChannel } = this;

    if (!selectedChannel || this.messageTypeOptions.length === 1) {
      return false;
    }

    if (this.record.isAirbnbOfficial) {
      return this.isSmsSelected && selectedChannel.reason === 'not_requested';
    }

    if (this.isSmsSelected) {
      return ['rejected', 'not_requested', 'invalid_receiver_phone'].includes(
        selectedChannel.reason,
      );
    }

    if (this.isEmailSelected) {
      return selectedChannel.reason === 'invalid_receiver_email';
    }

    return false;
  }

  get sendRequestTemplateId(): string {
    if (this.record.isAirbnbOfficial) {
      return 'airbnb_sms_permission';
    }

    return this.isEmailSelected ? 'contact_details_request' : 'sms_consent';
  }

  @cached
  get saveReplyRoute(): string {
    return getSaveReplyRouteFor(this.router.currentRouteName ?? '');
  }

  @cached
  get editDetailsRoute(): string {
    return getEditDetailsRouteFor(this.router.currentRouteName ?? '');
  }

  @cached
  get savedRepliesRoute(): string {
    return getSavedRepliesRouteFor(this.router.currentRouteName ?? '');
  }

  @action
  toggleTranslations() {
    this.showTranslations = !this.showTranslations;
  }

  @action
  handleRemoveImage() {
    this.uploadedImage = null;
    this.uploadedImagePreview = null;
    this.uploadedMediaType = null;
    this.uploadedFileName = null;
  }

  @action
  handleSelectMessageType(type: string): void {
    this.changeset.type = type;

    if (!this.selectedChannel?.enabled) {
      this.currentAIDraft = undefined;
      this.changeset.content = '';
    }

    this.changeset.validate();
  }

  @action
  @waitFor
  async handleUploadImage(file: UploadFile): Promise<void> {
    if (file.size > this.attachmentMaxFileSize) {
      this.notifications.error(
        this.canUploadVrboAttachment
          ? 'action_bookings_messages.notifications.attachment_upload_size_limit'
          : 'action_bookings_messages.notifications.image_upload_size_limit',
      );

      return;
    }

    this.uploadedImage = null;
    this.uploadedMediaType = null;
    this.uploadedFileName = null;
    this.isUploadingImage = true;

    try {
      if (this.canUploadVrboAttachment) {
        const { s3Url } = await this.fileHandler.uploadFile(file);

        this.uploadedImage = s3Url;
        this.uploadedMediaType = file.file.type;
      } else {
        const result = await this.fileHandler.uploadAirbnbMedia(
          file,
          this.record,
        );

        this.uploadedImage = result.signedUrl;
        this.uploadedMediaType = result.mimeType;
      }

      this.uploadedFileName = file.file.name;
      this.uploadedImagePreview = await this.#fileToDataUrl(file.file);
    } catch {
      this.notifications.error();
    } finally {
      this.isUploadingImage = false;
    }
  }

  @action
  @waitFor
  async handleSubmitMessage(): Promise<void> {
    if (this.isSubmitMessageDisabled) {
      return;
    }

    await this.changeset.validate();

    if (this.changeset.isInvalid) {
      return;
    }

    const sentContent = this.changeset.content ?? '';
    const aiDraft = this.currentAIDraft;
    const isAiAssistedSend = Boolean(aiDraft?.id && sentContent);

    this.isSendingMessage = true;
    this.changeset.content = '';
    this.currentAIDraft = undefined;

    try {
      this.#scrollToBottom();

      const sentDraft =
        isAiAssistedSend && aiDraft
          ? await this.#sendViaAiDraft(aiDraft, sentContent)
          : await this.#sendAsNewMessage(sentContent);

      this.analytics.trackEvent(AnalyticsEvents.messageSent);

      if (this.isEnquiry) {
        this.analytics.trackEvent(AnalyticsEvents.enquirySent);
      }

      this.store.findAll('inbox-message', { reload: true });

      if (sentDraft) {
        const editPct = this.#editPercentageFromDraft(sentDraft);

        if (editPct >= 10) {
          this.#requestFeedback(sentDraft, 'sent_feedback', editPct > 50);
        }
      }
    } catch (error: unknown) {
      if (this.#isTooManyRequestsError(error)) {
        this.notifications.error(
          'action_bookings_messages.notifications.ai_draft_usage_limit',
        );
      } else {
        this.notifications.error();
      }
    } finally {
      this.isSendingMessage = false;

      this.handleRemoveImage();

      this.#scrollToBottom();
    }
  }

  @action
  handleCalculatePosition(
    trigger: HTMLDivElement,
    content: HTMLDivElement,
    destination: HTMLDivElement,
    options: CalculatePositionOptions,
  ) {
    const position = calculatePosition(trigger, content, destination, options);

    if (!isNone(position.style.top)) {
      position.style.top -= fromPx(getCssVariable('--size-l'));
    }

    return position;
  }

  @action
  @waitFor
  async handleSendRequest(): Promise<void> {
    const hasEnabledChannel = this.record.messagingChannels.some(
      (channel) => channel.enabled,
    );

    if (!hasEnabledChannel) {
      this.notifications.error(
        'action_bookings_messages.notifications.no_channel_available',
      );

      return;
    }

    const reply = await this.contextualReplyRepository.find(
      this.sendRequestTemplateId,
      this.record,
    );

    Object.assign(this.changeset, {
      type: this.record.preferredMessagingChannel,
      content: reply.content,
    });
  }

  @action
  handleQuickResponse(customPrompt: string): void {
    this.handleGenerateAIDraft({ customPrompt });
  }

  @action
  handleAIAssistClick(): void {
    this.handleGenerateAIDraft();
  }

  @action
  @waitFor
  async handleGenerateAIDraft({
    customPrompt,
    parentDraft,
  }: {
    customPrompt?: string;
    parentDraft?: ModelRegistry['ai-draft'];
  } = {}): Promise<void> {
    if (this.isAIDraftButtonDisabled) {
      return;
    }

    this.isGeneratingAIDraft = true;

    const prompt = customPrompt ?? this.changeset.content ?? undefined;

    try {
      const draftData: {
        booking?: ModelRegistry['booking'];
        bookingRequest?: ModelRegistry['booking-request'];
        enquiry?: ModelRegistry['enquiry'];
        channel?: string;
        customPrompt?: string;
        parentDraft?: ModelRegistry['ai-draft'];
      } = {};

      if (this.isBooking) {
        draftData.booking = this.record as ModelRegistry['booking'];
      } else if (this.isBookingRequest) {
        draftData.bookingRequest = this
          .record as ModelRegistry['booking-request'];
      } else if (this.isEnquiry) {
        draftData.enquiry = this.record as ModelRegistry['enquiry'];
      }

      draftData.channel = this.changeset.type ?? this.selectedMessageType;

      if (prompt) {
        draftData.customPrompt = prompt;
      }

      if (parentDraft) {
        draftData.parentDraft = parentDraft;
      }

      const draft = this.store.createRecord('ai-draft', draftData);

      await draft.save();

      if (!draft.actionable) {
        this.notifications.info(
          'action_bookings_messages.notifications.ai_draft_not_actionable',
        );
      } else if (draft.generatedContent) {
        this.changeset.content = draft.generatedContent;
        this.currentAIDraft = draft;
      }
    } catch (error: unknown) {
      const messageKey = this.#isTooManyRequestsError(error)
        ? 'action_bookings_messages.notifications.ai_draft_usage_limit'
        : 'action_bookings_messages.notifications.ai_draft_error';
      this.notifications.error(messageKey);
    } finally {
      this.isGeneratingAIDraft = false;
    }
  }

  @action
  handleContentInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;

    if (target.value === '' && this.currentAIDraft && !this.showFeedbackModal) {
      this.handleDiscardDraft();
    }
  }

  #isTooManyRequestsError(error: unknown): boolean {
    const status = (error as { errors?: { status?: string }[] })?.errors?.[0]
      ?.status;
    return status === '429';
  }

  // True when the error carries a 4xx/5xx response. A media-only 204 success
  // surfaces as a throw with no such error, so this lets us tell a real
  // failure apart from the no-content success path.
  #isHttpError(error: unknown): boolean {
    const errors = (error as { errors?: { status?: string }[] })?.errors;
    return Boolean(errors?.some((e) => /^[45]\d\d$/.test(e.status ?? '')));
  }

  #requestFeedback(
    draft: ModelRegistry['ai-draft'],
    action: 'discard' | 'regenerate' | 'sent_feedback',
    requireReason: boolean,
  ): void {
    this.pendingFeedback = { draft, action, requireReason };
    this.showFeedbackModal = true;
  }

  @action
  handleShowConflicts(): void {
    this.showConflictsModal = true;
  }

  @action
  handleDismissConflicts(): void {
    this.showConflictsModal = false;
  }

  @action
  handleConflictSourceClick(sourceType: string, reference: string): void {
    this.showConflictsModal = false;

    if (sourceType === 'knowledge_base_qa_pair') {
      this.router.transitionTo('ai-messaging.knowledge-center', {
        queryParams: {
          propertyIds: [this.record.property.id],
          highlightQaPairId: reference,
        },
      });
    } else if (sourceType === 'property_data') {
      const baseRoute = (this.router.currentRouteName ?? '').startsWith(
        'xcalendar',
      )
        ? 'xcalendar'
        : 'calendar';

      this.router.transitionTo(
        `${baseRoute}.properties.details.basics`,
        this.record.property.id,
      );
    }
  }

  @action
  handleDiscardDraft(): void {
    if (this.currentAIDraft) {
      this.#requestFeedback(this.currentAIDraft, 'discard', false);
    }
  }

  @action
  handleRegenerateDraft(): void {
    if (this.currentAIDraft) {
      this.#requestFeedback(this.currentAIDraft, 'regenerate', true);
    }
  }

  @action
  @waitFor
  async handleFeedbackSubmit(result: FeedbackResult): Promise<void> {
    await this.#completeFeedback(result);
  }

  @action
  @waitFor
  async handleFeedbackSkip(): Promise<void> {
    await this.#completeFeedback();
  }

  @action
  handleFeedbackCancel(): void {
    this.showFeedbackModal = false;
    this.pendingFeedback = undefined;
  }

  #getRegenerationCustomPrompt(result?: FeedbackResult): string | undefined {
    if (!result?.feedbackReason) {
      return undefined;
    }

    if (result.feedbackReason === 'other') {
      return result.feedbackCustomReason;
    }

    return this.intl.t(
      `action_bookings_messages.ai.feedback.reasons.${result.feedbackReason}`,
    );
  }

  async #completeFeedback(result?: FeedbackResult): Promise<void> {
    const pending = this.pendingFeedback;
    this.showFeedbackModal = false;
    this.pendingFeedback = undefined;

    if (!pending) return;

    const { draft, action } = pending;

    try {
      if (action !== 'sent_feedback') {
        draft.status = 'discarded';
      }
      if (result) {
        draft.positiveFeedback = result.positiveFeedback;
        if (result.feedbackReason) {
          draft.feedbackReason = result.feedbackReason;
        }
      }
      const hasChanges = action !== 'sent_feedback' || result !== undefined;
      if (hasChanges) {
        await draft.save();
      }
    } catch (error) {
      this.sentry.captureException(error);
    }

    switch (action) {
      case 'discard':
        this.currentAIDraft = undefined;
        this.changeset.content = '';
        break;
      case 'regenerate':
        this.currentAIDraft = undefined;
        await this.handleGenerateAIDraft({
          parentDraft: draft,
          customPrompt: this.#getRegenerationCustomPrompt(result),
        });
        break;
      case 'sent_feedback':
        break;
    }
  }

  @action
  @waitFor
  async handleAssignedUserChange(user: IAssignableUser | null): Promise<void> {
    const { inboxMessage } = this;

    if (!inboxMessage) {
      return;
    }

    inboxMessage.assignedUserId = user?.id ?? null;
    this.isAssigningUser = true;

    try {
      await inboxMessage.save();
    } catch {
      inboxMessage.rollbackAttributes();
      this.notifications.error();
    } finally {
      this.isAssigningUser = false;
    }
  }

  #lastPendingDraftRecordId?: string;

  loadAssignableUsers = modifier((): void => {
    const { inboxMessage } = this;

    if (!inboxMessage) {
      return;
    }

    this.isAssignableUsersUnavailable = false;

    runTask(
      this,
      async () => {
        try {
          await this.store.findRecord('inbox-message', inboxMessage.id, {
            reload: true,
          });
        } catch {
          this.isAssignableUsersUnavailable = true;
        }
      },
      0,
    );
  });

  loadPendingDraft = modifier((): void => {
    const recordId = this.record.id;

    if (this.#lastPendingDraftRecordId !== recordId) {
      this.#lastPendingDraftRecordId = recordId;
      this.hasPendingDraftLoaded = false;
    }

    if (this.hasPendingDraftLoaded) return;
    if (!this.isBooking) return;
    if (!this.booking.aiOccasionContext?.hasPendingDraft) return;
    if (!this.isAiDraftEnabled) return;

    runTask(
      this,
      () => {
        this.hasPendingDraftLoaded = true;
        this.handleGenerateAIDraft();
      },
      0,
    );
  });

  scrollToBottom = modifier((): void => {
    this.#scrollToBottom();
  });

  trackFooterHeight = modifier((element: HTMLElement): (() => void) => {
    const wrapper = element.closest('.rhs-wrapper') as HTMLElement | null;
    let observer: ResizeObserver | undefined;

    if (wrapper) {
      observer = new ResizeObserver(() => {
        const footerRect = element.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();
        const margin = fromPx(getCssVariable('--size-s'));
        const bottomOffset = wrapperRect.bottom - footerRect.top + margin;
        wrapper.style.setProperty('--footer-height', `${bottomOffset}px`);
      });

      observer.observe(element);
    }

    return () => observer?.disconnect();
  });

  async #sendViaAiDraft(
    draft: ModelRegistry['ai-draft'],
    content: string,
  ): Promise<ModelRegistry['ai-draft']> {
    this.pendingAiMessage = this.messageRepository.createRecord({
      content,
      inbound: false,
      type: this.changeset.type,
      createdAt: new Date(),
      status: MessageStatus.pending,
      [getRhsRecordKey(this.record)]: this.record,
    });

    draft.status = 'sent';
    draft.sentContent = content;
    draft.sentAt = new Date();

    await draft.save();
    const pendingMessage = this.pendingAiMessage;

    await this.record.reload();

    this.pendingAiMessage = undefined;
    pendingMessage?.unloadRecord();

    return draft;
  }

  async #sendAsNewMessage(content: string): Promise<null> {
    const hasMedia = Boolean(this.uploadedImage);
    const imagePreview = this.uploadedImagePreview;
    const message = this.messageRepository.createRecord({
      // Image-only sends carry no text. Send `null` (not '') so the API takes
      // its no-content success path and returns 204, rather than failing body
      // validation (which would surface as a 400).
      content: hasMedia && !content ? null : content,
      mediaUrl: this.uploadedImage,
      mediaType: this.uploadedMediaType,
      inbound: false,
      type: this.changeset.type,
      createdAt: new Date(),
      [getRhsRecordKey(this.record)]: this.record,
    });

    try {
      await message.save();
    } catch (error) {
      // A media-only message returns 204 No Content, which Ember Data surfaces
      // as a throw because the response has no id. Treat that as a successful
      // send and keep the record with the local preview so it renders
      // immediately. Only do this for a 2xx response — for a genuine 4xx/5xx
      // the image did not send, so we must not show a misleading preview.
      if (hasMedia && !message.id && !this.#isHttpError(error)) {
        message.image = imagePreview;
        message.status = MessageStatus.sent;
        message.userName = this.session.currentUser?.name ?? null;
        message.context = ContextType.conversation;
        return null;
      }
      throw error;
    }
    if (message.isSms) {
      this.#startMessageStatusPolling();
    }
    return null;
  }

  #fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new window.FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  #editPercentageFromDraft(draft: ModelRegistry['ai-draft']): number {
    const totalChanged =
      (draft.charactersAdded ?? 0) + (draft.charactersRemoved ?? 0);
    const baseLength = (draft.generatedContent ?? '').length || 1;

    return (totalChanged / baseLength) * 100;
  }

  #scrollToBottom(): void {
    runTask(
      this,
      () => {
        const container = this.messagesContainer;

        if (!container) {
          return;
        }

        container.scrollTop = container.scrollHeight;
      },
      0,
    );
  }

  #startMessageStatusPolling(attempt = 1): void {
    const delay = config.IS_TEST ? 100 : MESSAGE_POLLING_INTERVALS[attempt - 1];
    const lastMessage = this.messagesSorted[this.messagesSorted.length - 1];

    if (!delay || !lastMessage?.isProcessing) {
      return;
    }

    runTask(
      this,
      async () => {
        await this.record.reload();

        this.#startMessageStatusPolling(attempt + 1);
      },
      delay,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Messages': typeof UiBookingMessagesComponent;
  }
}
